/**
 * Der Text der Vollständigkeits-Bremse im Eingabefeld (21.10.2026, Martin 19:58; Auflagen des Sicherheitsprüfers).
 *
 * Die Bremse legt einen offenen Satz ins Feld, statt ihn zu senden (shared/satz-vollstaendig.ts). Dieser Text ist kein Tippen —
 * er sperrt das Mikro nach einer Frage nicht (lib/schmied-warten.ts `martinTippt`). Damit der nächste gesprochene Satz nicht
 * ohne die erste Hälfte hinausgeht, merkt sich die Seite den Text — mit Zeit und Zug:
 *
 * - `ausBremse`: das Feld hält UNVERÄNDERT den gemerkten Text (zählt nie als Tippen, ohne Zeitgrenze).
 * - `bremseWeg`: was der nächste gesprochene Satz tut — nur ≤ `BREMSE_ANHANG_MS` und im selben Zug:
 *     „zusammen“  das Mikro ging von selbst nach einer Frage auf → Feld + Gesprochenes als EINE Nachricht, Feld leer;
 *     „anhaengen“ sonst (Martin drückte selbst) → ans Feld angehängt, nichts gesendet;
 *     „normal“    kein (gültiger) Bremse-Text → wie bisher.
 * - Gemerkt wird nur, wenn das Feld VOR dem Einfügen leer war oder selbst gültiger Bremse-Text war — ein getippter Entwurf
 *   davor macht das Ganze zu Martins Entwurf.
 *
 * Rein: keine Seite, keine Uhr — app.ts reicht Feld, Zeit und Zug herein.
 */

export const BREMSE_ANHANG_MS = 60_000;

export interface BremseMerker {
  text: string;
  um: number;
  zug: number;
}

export type BremseWeg = "zusammen" | "anhaengen" | "normal";

/** Hält das Feld unverändert den Text der Bremse? */
export function ausBremse(m: BremseMerker | null, feld: string): boolean {
  return m !== null && feld === m.text && feld.trim() !== "";
}

/** Darf der nächste Satz noch dazugehören (≤ 60 s, selber Zug, Feld unverändert)? */
export function anhangGilt(m: BremseMerker | null, feld: string, jetzt: number, zug: number): boolean {
  if (!m || !ausBremse(m, feld) || m.zug !== zug) return false;
  const alter = jetzt - m.um;
  return Number.isFinite(alter) && alter >= 0 && alter <= BREMSE_ANHANG_MS;
}

/**
 * Nach dem Einfügen: der neue Merker (oder null). `vorher` = das Feld vor dem Einfügen, `alt` = der bisherige Merker.
 * Ein Folgesatz an gültigen Bremse-Text bleibt Bremse-Text (Zeit läuft neu); vor getipptem Entwurf nie.
 */
export function merkeBremse(o: { vorher: string; jetzt: string; alt: BremseMerker | null; um: number; zug: number }): BremseMerker | null {
  if (o.jetzt.trim() === "") return null;
  const vorherLeer = o.vorher.trim() === "";
  if (!vorherLeer && !anhangGilt(o.alt, o.vorher, o.um, o.zug)) return null;
  return { text: o.jetzt, um: o.um, zug: o.zug };
}

/** Was der nächste gesprochene Satz tut. `vonSelbstNachFrage`: das Mikro ging von selbst auf (nach Frage/Antwort). */
export function bremseWeg(o: { m: BremseMerker | null; feld: string; jetzt: number; zug: number; vonSelbstNachFrage: boolean }): BremseWeg {
  if (!anhangGilt(o.m, o.feld, o.jetzt, o.zug)) return "normal";
  return o.vonSelbstNachFrage ? "zusammen" : "anhaengen";
}

/**
 * Nachprüfung (21.10.2026): darf „zusammen“ JETZT senden? Nur, wenn das Feld seit dem Anhängen unverändert ist, kein neuer Druck
 * läuft (Zustand ruhig, keine Karte) und kein Auftrag das Feld sperrt. Sonst behandelt app.ts es wie „anhaengen“. Rein.
 */
export function zusammenJetztSenden(l: { weg: BremseWeg; feldUnveraendert: boolean; ruhe: boolean; karteOffen: boolean; feldGesperrt: boolean }): boolean {
  return l.weg === "zusammen" && l.feldUnveraendert && l.ruhe && !l.karteOffen && !l.feldGesperrt;
}
